import chevronRight from 'assets/images/icons/inputs/chevron-right.svg';
import ExplanationText from 'components/v2/ExplanationText';
import TitleAndBack from 'components/v2/TitleAndBack';
import { ActionPrimaryButton, ActionSecondaryButton } from 'components/v2/buttons';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { confirmQuestion } from 'components/v2/confirm-dialog/confirmQuestion';
import Help from 'components/v2/copy/Help';
import StepBadge from 'components/v2/copy/StepBadge';
import useCopyWizardLogic from 'components/v2/copy/wizard/useCopyWizardLogic';
import { HelpBox } from 'components/v2/help-boxes';
import { Formik } from 'formik';
import { StepStatus } from 'pages/wizard/types';
import { BlockUI } from 'primereact/blockui';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useState } from 'react';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { EPDCopyGroupStatus, EPDModel, VerificationType } from 'services/EpdClient';
import { Container, HyperLink } from 'styles/Styles.styled';
import Spinner from 'styles/spinner.styled';

import LicenseeAdminReviewCommentBox from 'components/v2/LicenseeAdminReviewCommentBox';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import ConfirmWithCommentDialog from 'components/v2/dialogs/ConfirmWithCommentDialog';
import LicenseeAdminReviewDialog from 'components/v2/dialogs/LicenseeAdminReviewDialog';
import StatusCommentFullViewDialog from 'components/v2/dialogs/StatusCommentFullViewDialog';
import { STATUS_COMMENT_MAX_LENGTH } from 'constants/constants';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ErrorTextBox,
  FormPanel,
  MainViewFull,
  ReadMoreInlineButton,
  Step,
  WizardHeaderButtonBox,
  WizardHeaderGrid,
} from '../../../styles/v2/Styles.styled';
import { withGreyBackground } from '../WithGreyBackground';

const CopyEpdWizard: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [showLicenseeAdminReviewDialog, setShowLicenseeAdminReviewDialog] = useState(false);
  const [showDeclinePublicationDialog, setShowDeclinePublicationDialog] = useState(false);
  const [showVerificationDeclineReasonFullCommentDialog, setShowVerificationDeclineReasonFullCommentDialog] =
    useState(false);

  const {
    history,
    copyGroupId,
    groupData,
    steps,
    helpButtonRef,
    onPublish,
    onSendToPublish,
    onConfirmCancelPublication,
    onSendToVerification,
    onConfirmVerification,
    onRetractVerification,
    onConfirmCancelVerification,
    onConfirmGroupDelete,
    onSendToPublishGroupByLicenseeAdmin,
    activeStepIndex,
    setActiveStepIndex,
    isExplanationVisible,
    setIsExplanationVisible,
    declinePublicationText,
    setDeclinePublicationText,
    setDeclinePublicationConfirmed,
    isUiBlocked,
    isUpdatingEpd,
  } = useCopyWizardLogic();

  const verificationDeclineReasonPostProcessed =
    groupData.verificationDeclineReason &&
    (groupData.verificationDeclineReason.length > STATUS_COMMENT_MAX_LENGTH
      ? groupData.verificationDeclineReason.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : groupData.verificationDeclineReason);

  const initialValues = { groupName: '', collection: null, singleCopy: false };
  const confirmDeleteGroup = () => {
    confirmDelete({
      caption: `Are you sure you want to delete the group "${groupData.name}"?`,
      description: <div>The EPD data will be lost and you will not be able to restore it.</div>,
      onConfirm: onConfirmGroupDelete,
    });
  };
  const confirmCancelVerification = () => {
    confirmDelete({
      caption: `Are you sure you want to cancel verification process?`,
      description: <div>The EPD group verification will be cancelled.</div>,
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      onConfirm: onConfirmCancelVerification,
    });
  };

  // useEffect(() => { #11916: Temporary enabled access to Copy Group for testing purposes
  // if (AuthService.isAdmin() || AuthService.isLicenseeAdmin()) {
  //   return;
  // }
  // confirmInfo({
  //   caption: 'Copy function is temporarily unavailable.',
  //   description: (
  //     <>
  //       <strong>Dear customers</strong>,
  //       <br />
  //       <br />
  //       Unfortunately, this feature will be temporarily unavailable for a few weeks due to necessary updates. We expect it
  //       to be back up and running after August.
  //       <br />
  //       <br />
  //       Thank you for your patience and understanding.
  //     </>
  //   ),
  //   rejectLabel: 'Go to dashboard',
  //   onReject: () => history.push(EpdRoutes.dashboard),
  // });
  // }, []);

  const confirmCancelPublication = () => {
    confirmAlert({
      caption: `Are you sure you want to cancel the publication of Copy Group ${groupData.name}?`,
      description: 'You may interrupt the EPD reviewing process.',
      acceptLabel: 'Confirm',
      onConfirm: onConfirmCancelPublication,
    });
  };

  const confirmDeclinePublication = () => {
    setShowDeclinePublicationDialog(true);
  };

  const explanationMessages = {
    Draft: {
      Default: (
        <>
          With copy EPD functionality you can easily create similar EPDs based on EPD source template.{' '}
          <HyperLink onClick={() => helpButtonRef?.current?.click()}>Read full instruction here</HyperLink>
        </>
      ),
    },
    Reserved: {
      Default: (
        <>
          Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can
          start the verification process by clicking the button "Start verification".
        </>
      ),
    },
    'Sent for verification': {
      Default: <>EPD verification process in progress now. Please wait for EPD Verifier's confirmation.</>,
    },
    Verified: {
      Default: (
        <>
          Your EPDs have been verified. If a certain EPD was declined after verification, you can update or unlink these EPDs
          from the group. You will be able to access the unlinked EPDs from your dashboard. If you update declined EPDs, the
          copy batch EPDs can be sent for verification. If you unlink a declined EPD from a copy batch, you can send the
          remaining approved EPDs for publishing.
        </>
      ),
    },
    'Sent for publication': { Default: <>No actions needed.</> },
    'Licensee review': {
      Default: (
        <>
          The EPD Copy Group is awaiting the initial Licensee review. After the review, the Secretariat will make the final
          decision and proceed with publication.
        </>
      ),
    },
    'Declined copy by Licensee Admin': {
      Default: (
        <>
          Your EPD copy group publishing has been declined. Once you update your EPD copies you will be able start
          verification or send to publish again.
        </>
      ),
    },
    'Declined publication': {
      Default: (
        <>
          Your EPD copy group publishing has been declined. Once you update your EPD copies you will be able start
          verification or send to publish again.
        </>
      ),
    },
  };

  const explanationMessagesAdmin = {
    Draft: {
      Default: (
        <>
          With copy EPD functionality you can easily create similar EPDs based on EPD source template.{' '}
          <HyperLink onClick={() => helpButtonRef?.current?.click()}>Read full instruction here</HyperLink>
        </>
      ),
    },
    Reserved: {
      Default: (
        <>
          Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can
          start the verification process by clicking the button "Start verification".
        </>
      ),
    },
    'Sent for verification': {
      Default: <>EPD verification process in progress now. Please wait for EPD Verifier's confirmation.</>,
    },
    Verified: {
      Default: (
        <>
          Your EPDs have been verified. If a certain EPD was declined after verification, you can update or unlink these EPDs
          from the group. You will be able to access the unlinked EPDs from your dashboard. If you update declined EPDs, the
          copy batch EPDs can be sent for verification. If you unlink a declined EPD from a copy batch, you can send the
          remaining approved EPDs for publishing.
        </>
      ),
    },
    'Sent for publication': {
      Default: (
        <>
          Please check that all information in the EPD copies is correct and set EPD initial version and validity dates under
          Step 4.
        </>
      ),
    },
    'Licensee review': {
      Default: (
        <>
          The EPD Copy Group is awaiting the initial Licensee review. After the review, the Secretariat will make the final
          decision and proceed with publication.
        </>
      ),
    },
    'Declined copy by Licensee Admin': { Default: <>No actions needed.</> },
    'Declined publication': { Default: <>No actions needed.</> },
  };

  const explanationMessagesLicenseeAdmin = {
    Draft: {
      Default: (
        <>
          With copy EPD functionality you can easily create similar EPDs based on EPD source template.{' '}
          <HyperLink onClick={() => helpButtonRef?.current?.click()}>Read full instruction here</HyperLink>
        </>
      ),
    },
    Reserved: {
      Default: (
        <>
          Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can
          start the verification process by clicking the button "Start verification".
        </>
      ),
    },
    'Sent for verification': {
      Default: <>EPD verification process in progress now. Please wait for EPD Verifier's confirmation.</>,
    },
    Verified: {
      Default: (
        <>
          Your EPDs have been verified. If a certain EPD was declined after verification, you can update or unlink these EPDs
          from the group. You will be able to access the unlinked EPDs from your dashboard. If you update declined EPDs, the
          copy batch EPDs can be sent for verification. If you unlink a declined EPD from a copy batch, you can send the
          remaining approved EPDs for publishing.
        </>
      ),
    },
    'Sent for publication': {
      Default: <>No actions needed.</>,
    },
    'Licensee review': {
      Default: (
        <>
          The EPD Copy Group is awaiting the initial Licensee review. After the review, the Secretariat will make the final
          decision and proceed with publication.
        </>
      ),
    },
    'Declined copy by Licensee Admin': { Default: <>No actions needed.</> },
    'Declined publication': { Default: <>No actions needed.</> },
  };

  const explanationMessagesVerifier = {
    Draft: {
      Default: (
        <>
          With copy EPD functionality you can easily create similar EPDs based on EPD source template.{' '}
          <HyperLink onClick={() => helpButtonRef?.current?.click()}>Read full instruction here</HyperLink>
        </>
      ),
    },
    Reserved: {
      Default: (
        <>
          Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can
          start the verification process by clicking the button "Start verification".
        </>
      ),
    },
    'Sent for verification': {
      3: (
        <>
          Please upload verification report for the each EPD and click “Approve” button. If certain EPDs doesn’t meet
          verification standards - click “Decline” button.
          <br />
          <br />
          Once all EPDs will be review please click “Confirm verification” button to confirm that verification process was
          finished.
        </>
      ),
      Default: (
        <>
          Please check that the uploaded EPD and filled information in Steps 1 and 2 correspond with the verified information
          in the LCA report and EPD document.
          <br /> After the review, please upload the verification report and click “Approve” button.
          <br /> If certain EPDs cannot be approved please click “Decline button”.
          <br /> When you finish with EPDs review and all EPDs are marked with correspond review status please click “Confirm
          verification” button.
        </>
      ),
    },
    Verified: { Default: <>EPD review has been complete. No actions needed.</> },
    'Sent for publication': { Default: <>EPD review has been complete. No actions needed.</> },
    'Licensee review': {
      Default: (
        <>
          The EPD Copy Group is awaiting the initial Licensee review. After the review, the Secretariat will make the final
          decision and proceed with publication.
        </>
      ),
    },
    'Declined copy by Licensee Admin': { Default: <>EPD review has been complete. No actions needed.</> },
    'Declined publication': { Default: <>EPD review has been complete. No actions needed.</> },
  };

  const onGoBack = () => {
    if (copyGroupId === undefined) {
      history.push(EpdLinks.dashboard());
    } else {
      confirmQuestion({
        caption: 'Do you want to leave the editing process?',
        description: (
          <div>
            All entered data will be saved.
            <p style={{ marginTop: '0.5rem' }}>You can access your EPD copy group from the dashboard.</p>
          </div>
        ),
        acceptLabel: 'Leave',
        rejectLabel: 'Cancel',
        onConfirm: () => {
          history.push(EpdLinks.dashboard());
        },
        closable: false,
        contentStyle: { paddingTop: '2.5rem' },
      });
    }
  };

  const statusValues: Record<EPDCopyGroupStatus, number> = {
    [EPDCopyGroupStatus.Draft]: 0,
    [EPDCopyGroupStatus.Reserved]: 2,
    [EPDCopyGroupStatus.SentForVerification]: 3,
    [EPDCopyGroupStatus.Verified]: 4,
    [EPDCopyGroupStatus.SentForPublication]: 5,
    [EPDCopyGroupStatus.SentForPublicationLicenseeAdmin]: 5,
    [EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin]: 5,
    [EPDCopyGroupStatus.DeclinedPublication]: 5,
  };

  const getStepStatus = (stepNo: number) => {
    if (stepNo === activeStepIndex) {
      return StepStatus.Active;
    }
    if (stepNo < statusValues[groupData?.status || EPDCopyGroupStatus.Draft]) {
      return StepStatus.Done;
    }
    return StepStatus.Todo;
  };

  const getExplanationMessage = () => {
    if (
      groupData?.verificationType === VerificationType.EPDProcessCertification &&
      groupData?.status === EPDCopyGroupStatus.Reserved
    ) {
      return groupData?.epds?.every((item: EPDModel) => item.epdState?.isMandatoryComplete)
        ? 'Please upload your process certificate and verification report for each of the EPDs. Once all verification reports will be uploaded please accept service agreement in Step 4 and click “Send to publish” button.'
        : 'Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can start the process certification verification under Step 3 - EPD verification.';
    }
    if (
      groupData?.verificationType === VerificationType.EPDPreverifiedTool &&
      groupData?.status === EPDCopyGroupStatus.Reserved
    ) {
      return groupData?.epds?.every((item: EPDModel) => item.epdState?.isMandatoryComplete)
        ? 'Please accept service agreement in Step 4 and click “Send to publish” button.'
        : 'Please fill-in the information under Step 2 - Product information. Once you filled out all mandatory data you can start the publication under Step 4 - EPD publication.';
    }

    const messages =
      groupData.verifierId === AuthService.getUser()?.id
        ? explanationMessagesVerifier[groupData.status || EPDCopyGroupStatus.Draft]
        : AuthService.isAdmin()
        ? explanationMessagesAdmin[groupData.status || EPDCopyGroupStatus.Draft]
        : AuthService.isLicenseeAdmin()
        ? explanationMessagesLicenseeAdmin[groupData.status || EPDCopyGroupStatus.Draft]
        : explanationMessages[groupData.status || EPDCopyGroupStatus.Draft];

    const result = Object.getOwnPropertyDescriptor(messages, activeStepIndex)?.value || messages['Default'];

    return result;
  };

  return (
    <Container>
      <BlockUI
        blocked={isUiBlocked}
        fullScreen
        template={<ProgressSpinner style={{ width: '80px', height: '80px' }} strokeWidth="4" />}
      />
      {isUpdatingEpd && <Spinner />}
      <ConfirmDialog />
      {isUiBlocked === false && (
        <MainViewFull>
          <WizardHeaderGrid>
            <TitleAndBack
              title={groupData?.name ?? 'New EPD copy'}
              status={groupData?.status ?? EPDCopyGroupStatus.Draft}
              backTo={'Back to dashboard'}
              onBack={onGoBack}
            />
            <WizardHeaderButtonBox>
              <ActionSecondaryButton
                actionState={groupData?.groupState.delete}
                label="Delete group"
                onClick={confirmDeleteGroup}
                outlined
                type="reset"
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Start verification"
                onClick={onSendToVerification}
                actionState={groupData?.groupState.startVerification}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Cancel verification"
                onClick={confirmCancelVerification}
                actionState={groupData?.groupState.cancelVerification}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Confirm verification"
                onClick={onConfirmVerification}
                actionState={groupData?.groupState.confirmVerification}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Retract verification"
                onClick={onRetractVerification}
                actionState={groupData?.groupState.retractVerification}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Send to publish"
                onClick={onSendToPublish}
                actionState={groupData?.groupState.sendToPublish}
              />
              <ActionSecondaryButton
                style={{ marginLeft: '16px' }}
                label="Cancel publication"
                onClick={confirmCancelPublication}
                actionState={groupData?.groupState.cancelPublication}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Send to publish"
                onClick={() => setShowLicenseeAdminReviewDialog(true)}
                actionState={groupData?.groupState.licenseeAdminSendToPublish}
              />
              <ActionSecondaryButton
                style={{ marginLeft: '16px' }}
                label="Decline"
                onClick={confirmDeclinePublication}
                actionState={groupData?.groupState.declineCopy}
              />
              <ActionPrimaryButton
                style={{ marginLeft: '16px' }}
                label="Publish"
                onClick={onPublish}
                actionState={groupData?.groupState.publish}
              />
              <LicenseeAdminReviewDialog
                isOpened={showLicenseeAdminReviewDialog}
                onClose={(payload) => {
                  setShowLicenseeAdminReviewDialog(false);
                  if (payload) {
                    onSendToPublishGroupByLicenseeAdmin(payload);
                  }
                }}
              />
              <ConfirmWithCommentDialog
                isOpened={showDeclinePublicationDialog}
                onClose={(payload) => {
                  setShowDeclinePublicationDialog(false);
                  if (payload) {
                    setDeclinePublicationText(payload.commentText);
                    setDeclinePublicationConfirmed(true);
                  }
                }}
                header="Decline EPD Copy Group publication"
                help={
                  <HelpBox>
                    <span>
                      Please describe the reason to decline publication of <strong>{groupData.name}</strong>.
                    </span>
                    <span>The EPD Owner will be informed via email about the reason of declined publication.</span>
                  </HelpBox>
                }
                inputLabelText="Decline comment"
                primaryButtonText="Decline"
                isCommentRequired={true}
                maxLength={2000}
              />
            </WizardHeaderButtonBox>
          </WizardHeaderGrid>

          {(groupData.status === EPDCopyGroupStatus.DeclinedPublication ||
            groupData.status === EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin) && (
            <>
              <ErrorTextBox style={{ paddingLeft: 16 }}>
                <ErrorTitle>{t('epdWizard.epdState.action.commentBySecretariatTitle')}</ErrorTitle>
                {verificationDeclineReasonPostProcessed ||
                  'EPD copies publication were cancelled. Please contact the secretariat for further information.'}{' '}
                {verificationDeclineReasonPostProcessed &&
                  verificationDeclineReasonPostProcessed.length > STATUS_COMMENT_MAX_LENGTH && (
                    <ReadMoreInlineButton onClick={() => setShowVerificationDeclineReasonFullCommentDialog(true)}>
                      Read more
                    </ReadMoreInlineButton>
                  )}
              </ErrorTextBox>
              <StatusCommentFullViewDialog
                headerTitle={t('epdWizard.epdState.action.commentBySecretariatTitle')}
                comment={groupData.verificationDeclineReason!}
                isOpened={showVerificationDeclineReasonFullCommentDialog}
                onClose={() => setShowVerificationDeclineReasonFullCommentDialog(false)}
              />
            </>
          )}

          {groupData.status === EPDCopyGroupStatus.SentForPublication && groupData.licenseeAdminReviewComment && (
            <LicenseeAdminReviewCommentBox comment={groupData.licenseeAdminReviewComment} />
          )}

          {isExplanationVisible && (
            <ExplanationText onCloseButtonClick={() => setIsExplanationVisible(false)}>
              {getExplanationMessage()}
            </ExplanationText>
          )}
          <FormPanel style={{ padding: '12px 30px' }}>
            {steps.map((tab, index) => (
              <React.Fragment key={index}>
                {index > 0 && <img src={chevronRight} alt=">" />}
                <Step key={index} onClick={() => setActiveStepIndex(index + 1)}>
                  <StepBadge
                    stepNumber={index + 1}
                    isSelected={index + 1 === activeStepIndex}
                    status={getStepStatus(index + 1)}
                  />
                  <div>{tab.label}</div>
                </Step>
              </React.Fragment>
            ))}
          </FormPanel>

          <Formik initialValues={initialValues} validationSchema={null} onSubmit={(values) => {}}>
            {() => <React.Fragment key={groupData?.name}>{steps[activeStepIndex - 1].render()}</React.Fragment>}
          </Formik>
          <Help helpButtonRef={helpButtonRef} backLabel="Go back" />
        </MainViewFull>
      )}
    </Container>
  );
};

const ErrorTitle = styled.span`
  color: ${(props) => props.theme.colors.statusLabelRedText};
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  margin-right: 24px;
`;

export default withGreyBackground(CopyEpdWizard);
